import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { XIcon, CopySimpleIcon, PrinterIcon, ShoppingCartIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

/**
 * Activación de clientes: MAPA DE COMPRAS y QUÉ OFRECER.
 * Nota breve para la visita o llamada: qué ha comprado el cliente (producto, presentación, cantidades,
 * veces y fechas) y sugerencias de qué ofrecerle. Fuente: las mismas ventas que los reportes.
 */
const cant = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("es-HN", { maximumFractionDigits: 2 }));

function notaTexto(d) {
  const c = d.cliente, r = d.resumen;
  const out = [
    `ACTIVACIÓN · ${c.nombre}${c.codigo ? ` (${c.codigo})` : ""}`,
    `Región ${c.region || "—"}${c.rtc ? ` · RTC ${c.rtc}` : ""}${c.nivel_precio ? ` · Nivel ${c.nivel_precio}` : ""}`,
    r.ultima_compra
      ? `Última compra: ${fmtDate(r.ultima_compra)} (hace ${r.dias_sin_comprar} días) · ${r.compras} compras · ${r.productos_distintos} productos · ${fmtL(r.monto_total)}`
      : "Este cliente no tiene compras registradas.",
    "",
  ];
  if (d.ofrecer.recompra.length) {
    out.push("QUÉ OFRECER — RECOMPRA");
    d.ofrecer.recompra.forEach((o) => out.push(`• ${o.item} [${o.unidad}] — sugerido ${cant(o.cantidad_sugerida)} · últ. precio ${fmtL(o.ultimo_precio)} · ${o.motivo}`));
    out.push("");
  }
  if (d.ofrecer.temporada.length) {
    out.push("QUÉ OFRECER — TEMPORADA");
    d.ofrecer.temporada.forEach((o) => out.push(`• ${o.item} [${o.unidad}] — sugerido ${cant(o.cantidad_sugerida)} · ${o.motivo}`));
    out.push("");
  }
  if (d.ofrecer.region.length) {
    out.push(`LO QUE COMPRA SU REGIÓN (${c.region}) Y ÉL NO`);
    d.ofrecer.region.forEach((o) => out.push(`• ${o.item} [${o.unidad}] — lo compran ${o.clientes_region} clientes · ref. ${fmtL(o.precio_referencia)}`));
    out.push("");
  }
  if (d.productos.length) {
    out.push("LO QUE HA COMPRADO");
    d.productos.forEach((p) => out.push(`• ${p.item} [${p.unidad}] — ${cant(p.cantidad_neta)} netas en ${p.veces} compra(s) · última ${fmtDate(p.ultima_compra)} (${cant(p.ultima_cantidad)} a ${fmtL(p.ultimo_precio)})`));
  }
  return out.join("\n");
}

export default function ClientPurchaseMap({ clienteId, onClose }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/calendar/activacion/${clienteId}/mapa`)
      .then((r) => setD(r.data))
      .catch((e) => setError(apiErr(e) || "No se pudo cargar el historial del cliente"));
  }, [clienteId]);

  const copiar = async () => {
    try { await navigator.clipboard.writeText(notaTexto(d)); toast.success("Nota copiada"); }
    catch { toast.error("No se pudo copiar"); }
  };

  const imprimir = () => {
    const w = window.open("", "_blank");
    if (!w) return toast.error("Permite las ventanas emergentes para imprimir");
    const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Activación · ${esc(d.cliente.nombre)}</title>
      <style>body{font-family:Inter,Segoe UI,Arial,sans-serif;font-size:12px;margin:24px;color:#111}pre{white-space:pre-wrap;font-family:inherit;line-height:1.5}</style>
      </head><body><pre>${esc(notaTexto(d))}</pre><p style="color:#666;font-size:10px">${esc(d.nota_metodo)} Generado ${esc(fmtDate(d.generado))}.</p></body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="purchase-map-modal" {...vm.overlayProps}>
      <div className="bg-card border border-blue-500/30 rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-border/50 sticky top-0 bg-card z-10 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center flex-shrink-0">
              <ShoppingCartIcon size={22} weight="duotone" className="text-blue-400" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-blue-400 font-semibold">Activación · mapa de compras</div>
              <h3 className="text-lg font-bold truncate">{d?.cliente?.nombre || "Cargando…"}</h3>
              {d && <div className="text-xs text-text-muted">
                {d.cliente.codigo && <>{d.cliente.codigo} · </>}Región {d.cliente.region || "—"}{d.cliente.rtc && <> · RTC {d.cliente.rtc}</>}
                {d.cliente.nivel_precio && <> · Nivel {d.cliente.nivel_precio}</>}{d.cliente.telefono && <> · Tel. {d.cliente.telefono}</>}
              </div>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {d && <>
              <button onClick={copiar} data-testid="map-copiar" className="text-xs border border-border rounded-md px-3 py-1.5 flex items-center gap-1.5 hover:border-blue-400/60"><CopySimpleIcon size={14} /> Copiar nota</button>
              <button onClick={imprimir} data-testid="map-imprimir" className="text-xs border border-border rounded-md px-3 py-1.5 flex items-center gap-1.5 hover:border-blue-400/60"><PrinterIcon size={14} /> Imprimir / PDF</button>
            </>}
            <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary p-1" data-testid="map-cerrar"><XIcon size={18} /></button>
          </div>
        </div>

        {error && <div className="p-6 text-sm text-red-400">{error}</div>}
        {!d && !error && <div className="p-10 text-center text-sm text-text-muted">Revisando el historial de compras…</div>}
        {d && (
          <div className="p-5 space-y-5">
            {/* Resumen */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="map-resumen">
              {[
                ["Última compra", d.resumen.ultima_compra ? `${fmtDate(d.resumen.ultima_compra)}` : "Nunca"],
                ["Días sin comprar", d.resumen.dias_sin_comprar ?? "—"],
                ["Compras", d.resumen.compras],
                ["Productos distintos", d.resumen.productos_distintos],
                ["Total comprado", fmtL(d.resumen.monto_total)],
              ].map(([l, v]) => (
                <div key={l} className="bg-app border border-border/50 rounded-md p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">{l}</div>
                  <div className="text-sm font-semibold">{v}</div>
                </div>
              ))}
            </div>
            {d.resumen.por_anio.length > 1 && (
              <div className="text-xs text-text-secondary">Por año: {d.resumen.por_anio.map((a) => `${a.anio} ${fmtL(a.monto)}`).join(" · ")}</div>
            )}

            {/* Qué ofrecer */}
            <div className="space-y-3" data-testid="map-ofrecer">
              <h4 className="text-sm font-bold uppercase tracking-wider text-blue-400">Qué ofrecer</h4>
              {!d.ofrecer.recompra.length && !d.ofrecer.temporada.length && !d.ofrecer.region.length && (
                <div className="text-xs text-text-muted">Sin datos suficientes para sugerir productos.</div>
              )}
              <Sugerencias titulo="Recompra (lo que ya le compra a AGROPORT)" items={d.ofrecer.recompra} testid="ofrecer-recompra"
                extra={(o) => <>Sugerido <b>{cant(o.cantidad_sugerida)}</b> · últ. precio {fmtL(o.ultimo_precio)} ({fmtDate(o.ultima_compra)})</>}
                destacar={(o) => o.le_toca} />
              <Sugerencias titulo="Temporada (lo compró en estos meses otros años)" items={d.ofrecer.temporada} testid="ofrecer-temporada"
                extra={(o) => <>Sugerido <b>{cant(o.cantidad_sugerida)}</b> · últ. precio {fmtL(o.ultimo_precio)}</>} />
              <Sugerencias titulo={`Lo que compra su región (${d.cliente.region || "—"}) y él no`} items={d.ofrecer.region} testid="ofrecer-region"
                extra={(o) => <>Lo compran <b>{o.clientes_region}</b> clientes de la región · ref. {fmtL(o.precio_referencia)}</>}
                motivo={() => ""} />
            </div>

            {/* Lo que ha comprado */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-text-secondary mb-2">Lo que ha comprado ({d.productos.length})</h4>
              {d.productos.length === 0 ? (
                <div className="text-xs text-text-muted">Este cliente no tiene compras registradas.</div>
              ) : (
                <div className="overflow-x-auto border border-border/40 rounded-md">
                  <table className="w-full text-xs" data-testid="map-productos">
                    <thead className="bg-app text-text-muted uppercase text-[10px]">
                      <tr>
                        <th className="p-2 text-left">Producto</th><th className="p-2 text-left">Unidad</th>
                        <th className="p-2 text-right">Cant. neta</th><th className="p-2 text-right">Veces</th>
                        <th className="p-2 text-right">Prom./compra</th><th className="p-2 text-left">Última compra</th>
                        <th className="p-2 text-right">Últ. precio</th><th className="p-2 text-right">Total</th><th className="p-2 text-left">Meses habituales</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.productos.map((p) => (
                        <tr key={`${p.producto_id}|${p.unidad}`} className="border-t border-border/20">
                          <td className="p-2"><div className="font-medium">{p.item}</div><div className="text-[10px] text-text-muted">{p.codigo} · {p.categoria}</div></td>
                          <td className="p-2">{p.unidad}</td>
                          <td className="p-2 text-right font-mono">{cant(p.cantidad_neta)}{p.cantidad_devuelta ? <div className="text-[10px] text-text-muted">devolvió {cant(p.cantidad_devuelta)}</div> : null}</td>
                          <td className="p-2 text-right">{p.veces}</td>
                          <td className="p-2 text-right font-mono">{cant(p.promedio_por_compra)}</td>
                          <td className="p-2 whitespace-nowrap">{fmtDate(p.ultima_compra)} · {cant(p.ultima_cantidad)}<div className="text-[10px] text-text-muted">hace {p.dias_desde_ultima} días{p.intervalo_dias ? ` · cada ~${p.intervalo_dias} d` : ""}</div></td>
                          <td className="p-2 text-right font-mono">{fmtL(p.ultimo_precio)}</td>
                          <td className="p-2 text-right font-mono">{fmtL(p.monto)}</td>
                          <td className="p-2 text-[10px] text-text-secondary">{p.meses_habituales.join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <p className="text-[10px] text-text-muted">{d.nota_metodo}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Sugerencias({ titulo, items, extra, destacar, testid, motivo }) {
  if (!items.length) return null;
  return (
    <div data-testid={testid}>
      <div className="text-[11px] font-semibold text-text-secondary mb-1">{titulo}</div>
      <div className="grid md:grid-cols-2 gap-2">
        {items.map((o) => (
          <div key={`${o.producto_id}|${o.unidad}`} className={`border rounded-md p-2.5 text-xs ${destacar && destacar(o) ? "border-agro-green/50 bg-agro-green/5" : "border-border/50 bg-app"}`}>
            <div className="font-medium">{o.item} <span className="text-text-muted">[{o.unidad}]</span></div>
            <div className="text-text-secondary mt-0.5">{extra(o)}</div>
            {(motivo ? motivo(o) : o.motivo) && <div className="text-[10px] text-agro-yellow/90 mt-0.5">{motivo ? motivo(o) : o.motivo}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
